<template>
    <LoaderComponent v-if="equipements.length === 0 && isLoadingEquipements" />

    <table v-else class="equipement-table">
        <thead>
            <tr>
                <th>
                    <div v-if="allowSelectAll" class="radio">
                        <input
                            :aria-label="$t('typeSelection.selectionner.tout')"
                            :checked="isAllSelected"
                            :disabled="isSelectingAll"
                            :indeterminate="isPartiallySelected"
                            class="checkbox"
                            type="checkbox"
                            @click.stop="toggleSelectAll"
                        />
                    </div>
                </th>
                <DataTableHeader
                    v-for="header in tableHeader"
                    :key="header.label"
                    :header="header"
                    class="column"
                    @sort="doSort"
                ></DataTableHeader>
            </tr>
        </thead>
        <tbody v-if="equipements.length > 0">
            <tr
                v-for="equipement in equipements"
                :key="equipement.phaseId"
                class="pointer"
                v-on:click.stop="toggleSelect(equipement)"
            >
                <td>
                    <div v-if="displayRadio" class="radio">
                        <RadioBox :selected="equipement.phaseId === selectedEquipement?.phaseId" />
                    </div>
                    <div v-else-if="multiple" class="radio">
                        <input
                            :aria-label="$t('equipementSelection.search.select')"
                            :checked="selectedIdsComputed.includes(equipement.phaseId)"
                            :disabled="excludedEquipementIds.includes(String(equipement.id))"
                            class="checkbox"
                            type="checkbox"
                            @click.stop="toggleMulti(equipement)"
                        />
                    </div>
                </td>
                <td>
                    <div>
                        <span>
                            {{ equipement.libelle }}
                        </span>
                    </div>
                    <div class="item-info">
                        <span v-if="equipement.phaseLibelle && equipement.phaseLibelle.trim().toLowerCase() !== 'maintenance'">{{ equipement.phaseLibelle }}</span>
                    </div>
                </td>
                <td>
                    <div>
                        <span>
                            {{ equipement.groupeLibelle }}
                        </span>
                    </div>
                </td>
                <td>
                    <div>
                        <span>
                            {{ equipement.sousGroupeLibelle }}
                        </span>
                    </div>
                </td>
            </tr>
            <tr v-if="isLoadingEquipements">
                <td class="noDataTd" colspan="4">
                    {{ $t('search.isLoading') }}
                </td>
            </tr>
        </tbody>
        <tbody v-else-if="isLoadingEquipements">
            <tr>
                <td class="noDataTd" colspan="4">
                    {{ $t('search.isLoading') }}
                </td>
            </tr>
        </tbody>
        <tbody v-else>
            <tr>
                <td class="notFoundTd" colspan="4">
                    {{ $t('search.notFound') }}
                </td>
            </tr>
        </tbody>
    </table>
    <waiting-modal v-if="openModal && isLoadingInterventions" :text="$t('equipement.intervention.pending')" />
    <equipement-intervention-modal
        v-else-if="openModal && equipementInterventions.length > 0 && this.step === 2 && !openInfoEquipement"
        :equipementInterventions="equipementInterventions"
        @cancel="cancelInformation"
        @confirm="confirmInformation"
    />
    <equipement-condition
        v-else-if="openModal && this.step === 1 && !openInfoEquipement"
        :active="openModal && !isLoadingInterventions"
        @cancel="cancelCondition"
        @confirm="confirm"
    />
    <EquipementModal
        v-else-if="openModal && openInfoEquipement"
        :phase-id="this.phaseId"
        :preventif="false"
        @cancel="closeModal"
    />
</template>

<script lang="ts">
import RadioBox from '@/components/ui-components/RadioBox.vue';
import { defineComponent, PropType } from 'vue';
import { EQUIPEMENT, INTERVENTION } from '@/modules/Intervention/store/mutation-types';
import EquipementCondition from './EquipementCondition.vue';
import { EquipementItems } from '@/models/EquipementItems';
import { EquipementActions } from '@/modules/Intervention/store/modules/Equipement';
import { PreavisEquipementActions } from '@/modules/PreavisIntervention/store/modules/PreavisEquipement';
import WaitingModal from '../../../components/ui-components/WaitingModal.vue';
import EquipementInterventionModal from './EquipementInterventionModal.vue';
import DataTableHeader from '@/components/ui-components/DataTableHeader.vue';
import { SortType } from '@/models/SortType';
import { store } from '@/plugins/store';
import { TableHeader } from '@/models/TableHeader';
import EquipementModal from '@/modules/Preventif/components/EquipementModal.vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';

export default defineComponent({
    name: 'EquipementList',
    emits: ['sort', 'update:selectedIds'],
    data() {
        return {
            openModal: false,
            step: 0,
            phaseId: 0,
            isSelectingAll: false,
        };
    },
    props: {
        equipements: { type: Array as PropType<EquipementItems[]>, required: true },
        displayRadio: { type: Boolean, required: true },
        openInfoEquipement: { type: Boolean, default: false },
        multiple: { type: Boolean, default: false },
        selectedIds: { type: Array as PropType<number[]>, default: () => [] },
        excludedEquipementIds: { type: Array as PropType<string[]>, default: () => [] },
        allowSelectAll: { type: Boolean, default: false },
        equipementNamespace: { type: String, default: 'equipementModule' },
    },
    components: {
        LoaderComponent,
        DataTableHeader,
        RadioBox,
        EquipementCondition,
        WaitingModal,
        EquipementInterventionModal,
        EquipementModal,
    },
    computed: {
        selectedEquipement() {
            return store.getters.getInterventionEquipementValue;
        },
        selectedIdsComputed(): number[] {
            return (this.selectedIds as number[]) ?? [];
        },
        isLoadingInterventions() {
            return store.getters[`${this.equipementNamespace}/getIsLoadingInterventions`];
        },
        equipementInterventions() {
            return store.getters[`${this.equipementNamespace}/getEquipementInterventions`];
        },
        tableHeader(): TableHeader[] {
            return store.getters[`${this.equipementNamespace}/getTableHeader`];
        },
        isLoadingEquipements() {
            return store.getters[`${this.equipementNamespace}/getIsLoading`];
        },
        selectableEquipements(): EquipementItems[] {
            return this.equipements.filter(
                (eq: EquipementItems) => !this.excludedEquipementIds.includes(String(eq.id))
            );
        },
        isAllSelected(): boolean {
            if (this.selectableEquipements.length === 0) return false;
            return this.selectableEquipements.every((eq: EquipementItems) =>
                this.selectedIdsComputed.includes(eq.phaseId)
            );
        },
        isPartiallySelected(): boolean {
            return (
                !this.isAllSelected &&
                this.selectableEquipements.some((eq: EquipementItems) =>
                    this.selectedIdsComputed.includes(eq.phaseId)
                )
            );
        },
    },
    methods: {
        toggleSelect(equipement: EquipementItems) {
            if (this.multiple) {
                this.toggleMulti(equipement);
                return;
            }

            if (this.openInfoEquipement) {
                this.$router.push({
                    path: `/equipements/${equipement.phaseId}`,
                });
                return;
            }

            const pendingEquipement = equipement.id === this.selectedEquipement?.id ? undefined : equipement;
            this.$store.commit(INTERVENTION.SET_EQUIPEMENT, pendingEquipement);
            if (!this.openInfoEquipement) {
                if (pendingEquipement) {
                    this.$store.dispatch(
                        `equipementModule/${EquipementActions.GET_EQUIPEMENT_INTERVENTIONS}`,
                        pendingEquipement.phaseId
                    );
                    this.openModal = true;
                    this.step === 2 ? (this.step = 0) : this.step++;

                    if (this.step === 0) this.$store.commit(INTERVENTION.SET_ETAT_EQUIPEMENT, undefined);
                } else {
                    this.$store.commit(INTERVENTION.SET_ETAT_EQUIPEMENT, undefined);
                    this.step = 0;
                }
            }
        },
        toggleMulti(equipement: EquipementItems) {
            if (this.excludedEquipementIds.includes(String(equipement.id))) return;
            const id = equipement.phaseId;
            const current = [...this.selectedIdsComputed];
            const idx = current.indexOf(id);
            if (idx >= 0) current.splice(idx, 1);
            else current.push(id);
            this.$emit('update:selectedIds', current);
        },
        goIntervention() {
            this.$router.push('/intervention');
        },
        async toggleSelectAll() {
            if (this.isSelectingAll) return;

            if (this.isAllSelected) {
                this.$emit('update:selectedIds', []);
                return;
            }

            this.isSelectingAll = true;
            try {
                const ns = this.equipementNamespace;
                const page = store.getters[`${ns}/getPage`];
                const numberOfPages = store.getters[`${ns}/getNumberOfPages`];

                if (page + 1 < numberOfPages) {
                    const site = ns === 'preavisEquipementModule'
                        ? store.getters['preavisInterventionState/getPreavisSite']
                        : store.getters.getInterventionSiteValue;
                    const eqAction = ns === 'preavisEquipementModule'
                        ? PreavisEquipementActions.GET_EQUIPEMENT
                        : EquipementActions.GET_EQUIPEMENT;
                    for (let p = page + 1; p < numberOfPages; p++) {
                        await store.dispatch(`${ns}/${eqAction}`, {
                            adresseSiteId: site.id,
                            page: p,
                            equipementFilter: store.getters[`${ns}/getEquipementFilter`],
                            groupeFilter: store.getters[`${ns}/getGroupeFilter`],
                            ssGroupeFilter: store.getters[`${ns}/getSsGroupeFilter`],
                            sort: store.getters[`${ns}/getSort`],
                        });
                    }
                }

                const selectablePhaseIds = this.selectableEquipements.map((eq: EquipementItems) => eq.phaseId);
                this.$emit('update:selectedIds', selectablePhaseIds);
            } catch (error) {
                console.error('Erreur lors de la sélection de tous les équipements', error);
            } finally {
                this.isSelectingAll = false;
            }
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            this.$store.commit(`${this.equipementNamespace}/${EQUIPEMENT.SET_SORT}`, sort);
            this.$emit('sort');
        },
        confirmInformation() {
            this.$store.commit(`${this.equipementNamespace}/${EQUIPEMENT.SET_INTERVENTIONS}`, []);
            this.step = 0;
            this.openModal = false;
        },
        cancelCondition() {
            this.openModal = false;
            this.step = 0;
            this.$store.commit(INTERVENTION.SET_EQUIPEMENT, undefined);
            this.$store.commit(INTERVENTION.SET_ETAT_EQUIPEMENT, undefined);
        },
        closeModal() {
            this.openModal = false;
        },
        cancelInformation() {
            this.cancelCondition();
        },
        confirm() {
            if (this.step === 2 || this.$store.getters[`${this.equipementNamespace}/getEquipementInterventions`].length === 0) {
                this.step = 0;
            } else {
                this.step++;
            }
        },
    },
});
</script>

<style scoped>
.item-info {
    color: var(--medium-dark-grey);
}
.equipement-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    border-right: 1px solid var(--medium-dark-grey);
    border-left: 1px solid var(--medium-dark-grey);
    border-bottom: 1px solid var(--medium-dark-grey);
    border-radius: 3px;
    text-align: left;
}

.equipement-table thead {
    position: sticky;
    top: 0;
    font-size: 14px;
    color: var(--dark-grey);
    font-family: 'Raleway';
    font-variant-numeric: lining-nums;
    font-weight: 600;
}

.equipement-table thead th {
    position: sticky;
    top: 0;
    border-bottom: 1px solid var(--medium-dark-grey);
    border-top: 1px solid var(--medium-dark-grey);
    background-color: white;
    height: 52px;
}

.equipement-table thead th:first-child {
    border-top-left-radius: 3px;
    width: 3%;
}

.equipement-table thead th:last-child {
    border-top-right-radius: 3px;
}

.equipement-table tr td {
    border-bottom: 1px solid var(--medium-dark-grey);
    height: 52px;
    padding: 0 15px;
    overflow: hidden;
}

.equipement-table tbody tr:last-child td {
    border: none;
}

.equipement-table thead tr:first-child {
    border-top: none;
}

.radio {
    display: flex;
    justify-content: center;
}

.column {
    width: 32%;
}

.noDataTd {
    text-align: center;
}
.notFoundTd {
    text-align: center;
    height: 25vh;
}
.pointer {
    cursor: pointer;
}
</style>
